/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {GetterTree} from 'vuex';
import {RootState} from '@/store/types';
import {DlcInterface, FavouritesInterface, GameStateInterface, SpeciesInterface} from '@/store/modules/game/types';

/**
 * The game module getters provide entry points for stored data.
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const getters: GetterTree<GameStateInterface, RootState> = {
    /**
     * Game locked getter.
     * @param {GameStateInterface} state
     * @return {string}
     */
    locked(state: GameStateInterface): boolean {
        return state.locked;
    },

    /**
     * Gets the home zoo.
     * @param {GameStateInterface} state
     * @return {string}
     */
    zoo(state: GameStateInterface): string {
        return state.zoo;
    },

    /**
     * Gets the franchise cash value.
     * @param {GameStateInterface} state
     * @return {number}
     */
    cash(state: GameStateInterface): number {
        return state.cash;
    },

    /**
     * Gets the supported dlc list.
     * @param {GameStateInterface} state
     * @return {DlcInterface[]}
     */
    dlc(state: GameStateInterface): DlcInterface[] {
        return state.dlc;
    },

    /**
     * Gets the species.
     * @param {GameStateInterface} state
     * @return {SpeciesInterface[]}
     */
    species(state: GameStateInterface): SpeciesInterface[] {
        return state.species;
    },

    /**
     * Gets the species item by IRI.
     * @param {GameStateInterface} state
     * @return {(speciesId: string) => (SpeciesInterface | undefined)}
     */
    getSpeciesById(state: GameStateInterface): (speciesId: string) => SpeciesInterface|undefined {
        return (speciesId: string): SpeciesInterface|undefined => {
            return state.species.find((item: SpeciesInterface) => {
                return speciesId === item['@id'];
            });
        };
    },

    /**
     * Gets the species.
     * @param {GameStateInterface} state
     * @return {FavouritesInterface[]}
     */
    favourites(state: GameStateInterface): FavouritesInterface[] {
        return state.favourites;
    },

    /**
     * Determines if an animal is a favourite.
     * @param {GameStateInterface} state
     * @return {(speciesId: string) => boolean}
     */
    isFavourite(state: GameStateInterface): (animalId: string) => boolean {
        return (animalId: string): boolean => {
            return state.favourites.findIndex((item: FavouritesInterface) => {
                return animalId === item.animal;
            }) >= 0;
        };
    },

    /**
     * Gets the favourites item by animal id.
     * @param {GameStateInterface} state
     * @return {(speciesId: string) => (FavouritesInterface | undefined)}
     */
    getFavouriteByAnimalId(state: GameStateInterface): (animalId: string) => FavouritesInterface|undefined {
        return (animalId: string): FavouritesInterface|undefined => {
            return state.favourites.find((item: FavouritesInterface) => {
                return animalId === item.animal;
            });
        };
    },

    /**
     * Gold tweakable getter.
     * @param {GameStateInterface} state
     * @return {number | undefined}
     */
    gold(state: GameStateInterface): number|undefined {
        return state.tweakables.gradeGold;
    },

    /**
     * Silver tweakable getter.
     * @param {GameStateInterface} state
     * @return {number | undefined}
     */
    silver(state: GameStateInterface): number|undefined {
        return state.tweakables.gradeSilver;
    },

    /**
     * Bronze tweakable getter.
     * @param {GameStateInterface} state
     * @return {number | undefined}
     */
    bronze(state: GameStateInterface): number|undefined {
        return state.tweakables.gradeBronze;
    },

};
